import type { PropertySchema } from '@takeshape/schema';
import { Set } from 'immutable';
import toPath from 'lodash/toPath';
import type { RefObject } from 'react';
import type { MaybeImmutable } from '../common/utils/immutable-types';
import {
  ADD_ARRAY_ITEM,
  BLUR_FIELD,
  CHANGE_DISCRIMINATOR_FIELD,
  CHANGE_FIELD,
  CLEAR_FORM,
  CLEAR_PLACEHOLDER,
  COLLAPSE_ALL_ARRAY_ITEMS,
  EXPAND_ALL_ARRAY_ITEMS,
  FOCUS_FIELD,
  INIT_ARRAY,
  INSERT_PLACEHOLDER,
  REGISTER_FORM,
  REMOVE_ARRAY_ITEM,
  RESET_FORM,
  SET_BUSY,
  SET_FORM_DATA,
  SET_UI,
  SKIP_REGISTRATION,
  SUBMIT_FORM,
  SUBMIT_FORM_CANCELLED,
  SUBMIT_FORM_FAILURE,
  SUBMIT_FORM_SUCCESS,
  SWAP_ARRAY_ITEMS,
  TOGGLE_ARRAY_ITEM,
  VALIDATE_FORM
} from './action-types';
import type {
  Data,
  OnChangeField,
  OnSetFormData,
  OnSubmit,
  OnValidate,
  OnValidateFulfilled,
  SubmitFormSucessPayload,
  Validator
} from './types';

type Meta = Record<string, unknown>;

export function bindMeta(formName: string, path: string) {
  return <A extends any[], T extends string, M extends Meta, P = undefined>(
    actionCreator: (...args: A) => UnboundAction<T, M, P>
  ) =>
    (...args: A): FieldAction<T, M, P> => {
      const action = actionCreator(...args);

      return {
        ...action,
        meta: { ...action.meta, formName, path: toPath(path) }
      } as FieldAction<T, M, P>;
    };
}

export type FormMeta = {
  formName: string;
};

export type FieldMeta = {
  path: string[];
  silent?: boolean;
} & FormMeta;

export type UnboundAction<
  T extends string,
  M extends Meta = Record<string, unknown>,
  P = undefined
> = P extends undefined ? { type: T; meta: M } : { type: T; meta: M; payload: P };

export type FormAction<T extends string, M extends Meta = Record<string, unknown>, P = undefined> = UnboundAction<
  T,
  M & FormMeta,
  P
>;
export type FieldAction<T extends string, M extends Meta = Record<string, unknown>, P = undefined> = UnboundAction<
  T,
  M & FieldMeta,
  P
>;

export type ChangeFieldAction = FieldAction<typeof CHANGE_FIELD, Record<string, unknown>, unknown>;

export function changeField(formName: string, path: string, silent = false) {
  return (value: unknown): ChangeFieldAction => ({
    type: CHANGE_FIELD,
    meta: {
      formName,
      path: toPath(path),
      silent
    },
    payload: value
  });
}

export type ChangeDiscriminatorFieldAction = FieldAction<
  typeof CHANGE_DISCRIMINATOR_FIELD,
  Record<string, unknown>,
  unknown
>;

export function changeDiscriminatorField(formName: string, path: string, schemaPath: string[]) {
  return (value: unknown): ChangeDiscriminatorFieldAction => ({
    type: CHANGE_DISCRIMINATOR_FIELD,
    meta: {
      formName,
      path: toPath(path),
      schemaPath
    },
    payload: value
  });
}

export type SetFormDataAction = FieldAction<typeof SET_FORM_DATA, Record<string, unknown>, unknown>;

export function setFormData(formName: string, silent = false) {
  return (path: string[], value: unknown): SetFormDataAction => ({
    type: SET_FORM_DATA,
    meta: {
      formName,
      path,
      silent
    },
    payload: value
  });
}

export type FocusFieldAction = FieldAction<typeof FOCUS_FIELD>;

export function focusField(formName: string, path: string): FocusFieldAction {
  return {
    type: FOCUS_FIELD,
    meta: {
      formName,
      path: toPath(path)
    }
  };
}

export type BlurFieldAction = FieldAction<typeof BLUR_FIELD>;

export function blurField(formName: string, path: string): BlurFieldAction {
  return {
    type: BLUR_FIELD,
    meta: {
      formName,
      path: toPath(path)
    }
  };
}

export type SetUIAction = FieldAction<typeof SET_UI, Record<string, unknown>, unknown>;

export function setUI(formName: string, path: string, value: unknown): SetUIAction {
  return {
    type: SET_UI,
    meta: {
      formName,
      path: toPath(path)
    },
    payload: value
  };
}

export type SetBusyAction = FieldAction<typeof SET_BUSY, Record<string, unknown>, unknown>;

export function setBusy(formName: string, path: string, value: boolean): SetBusyAction {
  return {
    type: SET_BUSY,
    meta: {
      formName,
      path: toPath(path)
    },
    payload: value
  };
}

export type SwapArrayItemsAction = UnboundAction<
  typeof SWAP_ARRAY_ITEMS,
  {
    indexA: number;
    indexB: number;
  }
>;

export function swapArrayItems(indexA: number, indexB: number): SwapArrayItemsAction {
  return {
    type: SWAP_ARRAY_ITEMS,
    meta: {
      indexA,
      indexB
    }
  };
}

export type UnboundRemoveArrayItemAction = UnboundAction<typeof REMOVE_ARRAY_ITEM, { index: number }>;
export type RemoveArrayItemAction = FieldAction<typeof REMOVE_ARRAY_ITEM, { index: number }>;

export function removeArrayItem(index: number): UnboundRemoveArrayItemAction {
  return {
    type: REMOVE_ARRAY_ITEM,
    meta: {
      index
    }
  };
}

export type UnboundAddArrayItemAction = UnboundAction<typeof ADD_ARRAY_ITEM, { index?: number }, unknown>;
export type AddArrayItemAction = FieldAction<typeof ADD_ARRAY_ITEM, { index?: number }, unknown>;

export function addArrayItem(value: unknown, index?: number): UnboundAddArrayItemAction {
  return {
    type: ADD_ARRAY_ITEM,
    meta: { index },
    payload: value
  };
}

export type UnboundToggleArrayItemAction = UnboundAction<typeof TOGGLE_ARRAY_ITEM, Record<string, unknown>, string>;
export type ToggleArrayItemAction = FieldAction<typeof TOGGLE_ARRAY_ITEM, Record<string, unknown>, string>;

export function toggleArrayItem(key: string): UnboundToggleArrayItemAction {
  return {
    type: TOGGLE_ARRAY_ITEM,
    meta: {},
    payload: key
  };
}

export type UnboundClearArrayItemPlaceholderAction = UnboundAction<typeof CLEAR_PLACEHOLDER>;
export type ClearArrayItemPlaceholderAction = FieldAction<typeof CLEAR_PLACEHOLDER>;

export function clearArrayItemPlaceholder(): UnboundClearArrayItemPlaceholderAction {
  return {
    type: CLEAR_PLACEHOLDER,
    meta: {}
  };
}

export type UnboundInsertArrayItemPlaceholderAction = UnboundAction<
  typeof INSERT_PLACEHOLDER,
  { index: number },
  unknown
>;
export type InsertArrayItemPlaceholderAction = FieldAction<typeof INSERT_PLACEHOLDER, { index: number }, unknown>;

export function insertArrayItemPlaceholder(value: unknown, index: number): UnboundInsertArrayItemPlaceholderAction {
  return {
    type: INSERT_PLACEHOLDER,
    meta: {
      index
    },
    payload: value
  };
}

export type UnboundCollapseAllArrayItemsAction = UnboundAction<typeof COLLAPSE_ALL_ARRAY_ITEMS>;
export type CollapseAllArrayItemsAction = FieldAction<typeof COLLAPSE_ALL_ARRAY_ITEMS>;

export function collapseAllArrayItems(): UnboundCollapseAllArrayItemsAction {
  return {
    type: COLLAPSE_ALL_ARRAY_ITEMS,
    meta: {}
  };
}

export type UnboundExpandAllArrayItemsAction = UnboundAction<typeof EXPAND_ALL_ARRAY_ITEMS>;
export type ExpandAllArrayItemsAction = FieldAction<typeof EXPAND_ALL_ARRAY_ITEMS>;

export function expandAllArrayItems(): UnboundExpandAllArrayItemsAction {
  return {
    type: EXPAND_ALL_ARRAY_ITEMS,
    meta: {}
  };
}

export type UnboundInitializeArrayAction = UnboundAction<typeof INIT_ARRAY>;
export type InitializeArrayAction = FieldAction<typeof INIT_ARRAY>;

export type InitializeArrayMeta = {
  collapseByDefault?: boolean;
  formName: string;
  path: string[];
  /**
   * Initialize again even if array is already initialized
   */
  reinit?: boolean;
};

export function initializeArray(
  _formName: string,
  _path: string,
  meta: InitializeArrayMeta
): UnboundInitializeArrayAction {
  return {
    type: INIT_ARRAY,
    meta
  };
}

export type RegisterFormOptions<T extends Data> = {
  initialDirty?: Set<string> | string[];
  onChangeFieldRef?: RefObject<OnChangeField | undefined>;
  onSetFormDataRef?: RefObject<OnSetFormData | undefined>;
  onValidateRef?: RefObject<OnValidate | undefined>;
  onSubmitRef?: RefObject<OnSubmit<T> | undefined>;
  validatorRef?: RefObject<Validator<T> | undefined>;
  autoSubmit?: boolean;
  validateOnBlur?: boolean;
};

export type RegisterFormPayload<T extends Data> = {
  schema: MaybeImmutable<PropertySchema>;
  initialData: unknown;
  initialDirty?: Set<string>;
} & RegisterFormOptions<T>;

export type RegisterFormAction<T extends Data = Data> = FormAction<
  typeof REGISTER_FORM,
  Record<string, unknown>,
  RegisterFormPayload<T>
>;

export function registerForm<T extends Data = Data>(
  formName: string,
  schema: MaybeImmutable<PropertySchema>,
  initialData: unknown,
  options: Partial<RegisterFormOptions<T>> = {}
): RegisterFormAction<T> {
  const {
    initialDirty,
    onChangeFieldRef,
    onSetFormDataRef,
    onValidateRef,
    validatorRef,
    onSubmitRef,
    autoSubmit,
    validateOnBlur
  } = options;
  return {
    type: REGISTER_FORM,
    meta: {
      formName
    },
    payload: {
      schema,
      initialData,
      initialDirty: Array.isArray(initialDirty) ? Set(initialDirty) : initialDirty,
      onChangeFieldRef,
      onSetFormDataRef,
      onValidateRef,
      validatorRef,
      onSubmitRef,
      autoSubmit,
      validateOnBlur
    }
  };
}

export type SkipRegistrationAction = FormAction<typeof SKIP_REGISTRATION>;

export function skipRegistration(formName: string): SkipRegistrationAction {
  return {
    type: SKIP_REGISTRATION,
    meta: {
      formName
    }
  };
}

export type ResetFormPayload = {
  initialData?: unknown;
};

export type ResetFormAction = FormAction<typeof RESET_FORM, Record<string, unknown>, ResetFormPayload>;

export function resetForm(formName: string, options: ResetFormPayload = {}): ResetFormAction {
  return {
    type: RESET_FORM,
    payload: options,
    meta: {
      formName
    }
  };
}

export type ClearFormAction = FormAction<typeof CLEAR_FORM>;

export function clearForm(formName: string): ClearFormAction {
  return {
    type: CLEAR_FORM,
    meta: {
      formName
    }
  };
}

export type SubmitFormAction<T extends Data = Data> = {
  type: typeof SUBMIT_FORM;
  meta: {
    formName: string;
    onlyDirty?: boolean;
    withValues?: boolean;
    async?: boolean;
    invokedDuringAutoSubmit?: boolean;
  };
  callback?: OnSubmit<T>;
  validateCallback?: OnValidateFulfilled;
};

export type SubmitFormParams<T extends Data = Data> = {
  formName: string;
  callback?: OnSubmit<T>;
  validateCallback?: OnValidateFulfilled;
  onlyDirty?: boolean;
  withValues?: boolean;
  async?: boolean;
  invokedDuringAutoSubmit?: boolean;
};

export function submitForm<T extends Data = Data>(
  formName: string,
  callback?: OnSubmit<T>,
  onlyDirty?: boolean
): SubmitFormAction;
export function submitForm<T extends Data = Data>(params: SubmitFormParams<T>): SubmitFormAction;
export function submitForm<T extends Data = Data>(
  nameOrParams: string | SubmitFormParams<T>,
  callback?: OnSubmit<T>,
  onlyDirty?: boolean
): SubmitFormAction<T> {
  if (typeof nameOrParams === 'object') {
    const params = nameOrParams;
    return {
      type: SUBMIT_FORM,
      meta: {
        formName: params.formName,
        onlyDirty: params.onlyDirty,
        withValues: params.withValues,
        async: params.async,
        invokedDuringAutoSubmit: params.invokedDuringAutoSubmit
      },
      callback: params.callback,
      validateCallback: params.validateCallback
    };
  }

  return {
    type: SUBMIT_FORM,
    meta: {
      formName: nameOrParams,
      onlyDirty
    },
    callback
  };
}

export type SubmitFormSuccessAction = FormAction<
  typeof SUBMIT_FORM_SUCCESS,
  FormMeta,
  SubmitFormSucessPayload | undefined
>;

export function submitFormSuccess(formName: string, payload?: SubmitFormSucessPayload): SubmitFormSuccessAction {
  return {
    type: SUBMIT_FORM_SUCCESS,
    meta: {
      formName
    },
    payload
  };
}

export type SubmitFormCancelledAction = FormAction<typeof SUBMIT_FORM_CANCELLED>;

export function submitFormCancelled(formName: string): SubmitFormCancelledAction {
  return {
    type: SUBMIT_FORM_CANCELLED,
    meta: {
      formName
    }
  };
}

export type SubmitFormFailureAction = FormAction<typeof SUBMIT_FORM_FAILURE>;

export function submitFormFailure(formName: string): SubmitFormFailureAction {
  return {
    type: SUBMIT_FORM_FAILURE,
    meta: {
      formName
    }
  };
}

export type ValidateFormAction = FormAction<typeof VALIDATE_FORM>;

export function validateForm(formName: string): ValidateFormAction {
  return {
    type: VALIDATE_FORM,
    meta: {
      formName
    }
  };
}
